import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { brandColors } from '../../theme';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';
import { SEO } from '../../components/SEO';
import { generateBreadcrumbSchema } from '../../components/SEO/schemas';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Fade,
  CircularProgress,
  Alert,
  Link,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { 
  useContinents, 
  useCountriesByContinent, 
  type Country 
} from '../../hooks/useMarketCompassApi';

const createSlug = (name: string): string => {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};


const CountriesPage: React.FC = () => {
  const { continentName } = useParams<{ continentName: string }>();
  const { navigateTo } = useAppNavigation();
  const { hasStorePrivileged } = useUserPermissions();
  const [searchQuery, setSearchQuery] = useState('');
  
  const { data: continents = [], isLoading: loadingContinents } = useContinents();
  
  const selectedContinent = useMemo(() => {
    if (!continentName || continents.length === 0) {
      return undefined;
    }
    return continents.find(c => createSlug(c.name) === continentName);
  }, [continentName, continents]);

  const { 
    data: countries = [], 
    isLoading: loadingCountries, 
    error: countriesError 
  } = useCountriesByContinent(selectedContinent?.id);

  const filteredCountries = countries.filter(country => {
    // Filter out inactive countries for users without store.privileged permission
    if (!hasStorePrivileged && country.isActive === false) {
      return false;
    }
    return country.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Check if there are any inactive countries being displayed
  const hasInactiveCountries = filteredCountries.some(c => c.isActive === false);

  const handleCountrySelect = (country: Country) => {
    if (!selectedContinent) return;
    navigateTo(`/global-market-reports/${createSlug(selectedContinent.name)}/${createSlug(country.name)}`);
  };

  if (loadingContinents) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading markets...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!selectedContinent) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Continent not found. <Link component="button" onClick={() => navigateTo('/global-market-reports')} sx={{ cursor: 'pointer' }}>Go back to continents</Link>
        </Alert>
      </Container>
    );
  }

  if (loadingCountries) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading countries for {selectedContinent.name}...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (countriesError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Failed to load countries for {selectedContinent.name}. Please try again.
        </Alert>
      </Container>
    );
  }

  // Generate breadcrumb schema
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Global Market Reports', url: '/global-market-reports' },
    { name: selectedContinent.name, url: `/global-market-reports/${continentName}` }
  ]);

  return (
    <>
      <SEO
        title={`${selectedContinent.name} Hotel Market Reports & Analytics`}
        description={`Explore comprehensive hospitality market reports for ${selectedContinent.name}. Access detailed hotel analytics, performance metrics, ADR, RevPAR, and occupancy data for all countries in ${selectedContinent.name}.`}
        canonical={`/global-market-reports/${continentName}`}
        structuredData={breadcrumbSchema}
      />
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography
            component="h1"
            sx={{
              color: brandColors.blue,
              fontWeight: 400,
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.5px',
              mb: 2,
            }}
          >
            {selectedContinent.name} Countries with Published Markets
          </Typography>
        <Typography variant="h6" color="text.secondary">
          {selectedContinent.activeCountryCount} countries • {selectedContinent.activeMarketCount} markets
        </Typography>
      </Box>

      {/* Breadcrumbs */}
      <AppBreadcrumbs
        items={[
          { label: 'Continents', path: '/global-market-reports' },
          { label: selectedContinent.name }
        ]}
        sx={{ mb: 3 }}
      />

      {/* Show warning for inactive countries when viewed by special users */}
      {hasInactiveCountries && hasStorePrivileged && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            Special Access View
          </Typography>
          <Typography variant="body2">
            Some countries shown are flagged as Internal Only which means they are only visible to Horwath HTL licensees and not regular users.
          </Typography>
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder={`Search countries in ${selectedContinent.name}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>


      {/* Countries Grid */}
      <Fade in={true}>
        <Box>
          {filteredCountries.length === 0 ? (
            <Alert severity="info">
              No countries found matching "{searchQuery}"
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {filteredCountries.map((country, index) => (
                <Grid item xs={12} sm={6} md={4} key={country.id}>
                  <Fade in={true} timeout={200 + index * 100}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: 6 },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      onClick={() => handleCountrySelect(country)}
                    >
                      {country.isActive === false && (
                        <Chip
                          label="Internal Only"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: '#FFCC00',
                            color: 'black',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            zIndex: 1,
                          }}
                        />
                      )}
                      <CardContent>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          {country.code ? (
                            <Box sx={{ 
                              mb: 1, 
                              height: '5rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'visible'
                            }}>
                              <span 
                                className={`fi fi-${country.code.toLowerCase()} fis`} 
                                style={{ 
                                  fontSize: '4rem',
                                  display: 'inline-block',
                                  lineHeight: 1
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="h2" sx={{ mb: 1 }}>🏳️</Typography>
                          )}
                          <Typography variant="h6" fontWeight="bold">
                            {country.name}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {country.activeMarketCount} markets
                          </Typography>
                          <Typography variant="body2" color="primary" fontWeight="medium">
                            Explore →
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>
    </Container>
    </>
  );
};

export default CountriesPage;